import { showApiErrorNotification, useApiMutation } from '@vrplatform/data';
import { useCallback } from 'react';
import { downloadFromUrl } from '../utils';

type DownloadTransactionAttachmentInput = {
  attachmentId: string | null | undefined;
  filename?: string | null;
};

const DOWNLOAD_ERROR_TITLE = 'Failed to download attachment';

export const useTransactionAttachmentDownload = () => {
  const { mutateAsync: requestDownload, isPending } = useApiMutation(
    'get',
    '/transaction-attachments/{attachmentId}/download'
  );

  const download = useCallback(
    async ({ attachmentId, filename }: DownloadTransactionAttachmentInput) => {
      if (!attachmentId) {
        showApiErrorNotification({
          title: DOWNLOAD_ERROR_TITLE,
          error: new Error('Attachment is missing identifier.'),
        });
        return;
      }

      try {
        const res = await requestDownload({
          params: {
            path: {
              attachmentId,
            },
          },
          parseAs: 'blob',
        });

        if (!res.data) {
          throw new Error('No file returned for download.');
        }

        const resolvedFilename = ensureFilenameHasExtension(
          getFilenameFromContentDisposition(
            res.response.headers.get('content-disposition')
          ) ||
            filename ||
            'attachment',
          res.response.headers.get('content-type')
        );

        const url = window.URL.createObjectURL(res.data as any); // api endpoint is not correctly typed, it returns a readable stream
        downloadFromUrl(url, resolvedFilename);
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (error) {
        showApiErrorNotification({
          title: DOWNLOAD_ERROR_TITLE,
          error,
        });
      }
    },
    [requestDownload]
  );

  return { download, isPending };
};

const getFilenameFromContentDisposition = (value: string | null) => {
  if (!value) return null;

  const filenameStarMatch = /filename\*=UTF-8''([^;]+)/i.exec(value);
  if (filenameStarMatch?.[1]) {
    return decodeURIComponent(filenameStarMatch[1]);
  }

  const filenameMatch = /filename\s*=\s*"?([^";]+)"?/i.exec(value);
  return filenameMatch?.[1] ?? null;
};

const CONTENT_TYPE_EXTENSION_MAP: Record<string, string> = {
  'application/json': 'json',
  'application/msword': 'doc',
  'application/pdf': 'pdf',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'pptx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'application/zip': 'zip',
  'image/gif': 'gif',
  'image/heic': 'heic',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'text/csv': 'csv',
  'text/plain': 'txt',
};

const ensureFilenameHasExtension = (
  filename: string,
  contentType: string | null
) => {
  if (/\.[A-Za-z0-9]+$/.test(filename)) return filename;

  const extension = getExtensionFromContentType(contentType);
  if (!extension) return filename;

  return `${filename}.${extension}`;
};

const getExtensionFromContentType = (value: string | null) => {
  if (!value) return null;

  const mime = value.split(';')[0]?.trim().toLowerCase();
  if (!mime) return null;

  const mapped = CONTENT_TYPE_EXTENSION_MAP[mime];
  if (mapped) return mapped;

  const subtype = mime.split('/')[1];
  if (!subtype) return null;

  const cleaned = subtype.split('+')[0]?.trim();
  if (!cleaned) return null;

  if (cleaned.startsWith('vnd.') || cleaned.startsWith('x-')) {
    return null;
  }

  return cleaned;
};
